import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { TokenPair } from '../api/types';
import { PlatformApiError } from './errors';
import { tokenStorage } from '../auth/tokenStorage';
import { config } from '../config';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Do not surface a toast for this request's failure. */
    skipErrorToast?: boolean;
    /** Do not attempt a token refresh + retry on a 401. */
    skipAuthRefresh?: boolean;
  }
}

export interface HttpClientHooks {
  /** Called when the session can no longer be refreshed (expired/revoked). */
  onSessionExpired?: () => void;
  /** Optional global notification sink (wired to react-toastify at bootstrap). */
  notify?: (message: string, kind: 'error' | 'success') => void;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/bootstrap'];

let hooks: Required<HttpClientHooks> = { onSessionExpired: () => undefined, notify: () => undefined };

export function configureHttpClient(next: HttpClientHooks): void {
  hooks = { ...hooks, ...next };
}

function sessionExpiredError(): PlatformApiError {
  return new PlatformApiError({
    message: 'Session expired. Please sign in again.',
    code: 'session_expired',
    status: 401,
  });
}

// ---------------------------------------------------------------------------
// Refresh-token rotation — module level so API calls and the session layer
// share one single-flight in-flight rotation.
// ---------------------------------------------------------------------------

let rotating: Promise<TokenPair> | null = null;

async function callRefresh(organizationId: string, productCode: string): Promise<TokenPair> {
  const context = tokenStorage.getRefreshContext();
  if (!context) throw sessionExpiredError();
  const { data } = await axios.post<TokenPair>(`${config.apiBaseUrl}/auth/refresh`, {
    refreshToken: context.refreshToken,
    organizationId,
    productCode,
  });
  tokenStorage.applyTokenPair(data, { organizationId, productCode });
  return data;
}

function singleFlight(operation: () => Promise<TokenPair>): Promise<TokenPair> {
  if (!rotating) {
    rotating = operation().finally(() => {
      rotating = null;
    });
  }
  return rotating;
}

/** Rotate with the context bound to the current session (boot restore, 401 retry). */
export function refreshNow(): Promise<TokenPair> {
  return singleFlight(() => {
    const context = tokenStorage.getRefreshContext();
    if (!context) throw sessionExpiredError();
    return callRefresh(context.organizationId, context.productCode);
  });
}

/** Rotate while moving to another organization/product (organization switch). */
export function rotateWith(organizationId: string, productCode: string): Promise<TokenPair> {
  return singleFlight(() => callRefresh(organizationId, productCode));
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

let instance: AxiosInstance | null = null;

function buildClient(): AxiosInstance {
  const client = axios.create({ baseURL: config.apiBaseUrl, timeout: 20_000, withCredentials: true });

  client.interceptors.request.use((request) => {
    const access = tokenStorage.getAccessToken();
    if (access) request.headers.set('Authorization', `Bearer ${access}`);
    return request;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const requestConfig = error.config as
        | (RetriableConfig & { skipErrorToast?: boolean; skipAuthRefresh?: boolean })
        | undefined;
      const status = error.response?.status;
      const url = requestConfig?.url ?? '';
      const isAuthPath = AUTH_PATHS.some((path) => url.includes(path));
      const carriedAuth = Boolean(requestConfig?.headers?.Authorization);

      // 401 on a request that actually carried our bearer token -> rotate once,
      // then replay the original request. Refresh/login failures are never retried.
      if (
        status === 401 &&
        requestConfig &&
        !requestConfig._retried &&
        !requestConfig.skipAuthRefresh &&
        !isAuthPath &&
        carriedAuth
      ) {
        requestConfig._retried = true;
        try {
          const pair = await refreshNow();
          requestConfig.headers.set('Authorization', `Bearer ${pair.accessToken}`);
          return client.request(requestConfig);
        } catch (refreshFailure) {
          tokenStorage.clear();
          hooks.onSessionExpired();
          throw PlatformApiError.fromAxios(refreshFailure, 'Your session has expired. Please sign in again.');
        }
      }

      const mapped = PlatformApiError.fromAxios(error, 'The request failed.');
      if (!requestConfig?.skipErrorToast) {
        hooks.notify(mapped.message, 'error');
      }
      return Promise.reject(mapped);
    },
  );

  return client;
}

export function http(): AxiosInstance {
  if (!instance) instance = buildClient();
  return instance;
}
